/**
 * Shared utilities with zero browser side effects.
 */
export function now(): number {
  return Date.now();
}

export function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

export function safeJsonParse<T>(input: string, fallback: T): T {
  try {
    return JSON.parse(input) as T;
  } catch {
    return fallback;
  }
}

export function toBoolean(value: unknown): boolean {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") {
    return value === "1" || value.toLowerCase() === "true";
  }
  return false;
}

export function toNumber(value: unknown, fallback = 0): number {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function throttle<T extends (...args: any[]) => void>(
  fn: T,
  waitMs: number,
): T {
  let last = 0;
  let timeout: ReturnType<typeof setTimeout> | undefined;

  return ((...args: unknown[]) => {
    const time = now();
    const remaining = waitMs - (time - last);
    if (remaining <= 0) {
      last = time;
      fn(...args);
      return;
    }
    if (!timeout) {
      timeout = setTimeout(() => {
        last = now();
        timeout = undefined;
        fn(...args);
      }, remaining);
    }
  }) as T;
}

export function safeWindow(): Window | undefined {
  if (typeof window === "undefined") return undefined;
  return window;
}

export function safeDocument(): Document | undefined {
  if (typeof document === "undefined") return undefined;
  return document;
}

export function safeNavigator(): Navigator | undefined {
  if (typeof navigator === "undefined") return undefined;
  return navigator;
}
